import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Zap, CheckCircle2, XCircle, ArrowDown, Mail, Save, Target } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOURNEY = [
  { step: "Booking Confirmation", desc: "Instant email + SMS the moment a booking is submitted", keys: ["customer_booking_emails"] },
  { step: "Agreement Reminder", desc: "Follow-ups until the rental agreement is signed", keys: ["email_notifications", "sms_notifications"] },
  { step: "Payment Reminder", desc: "Balance-due nudges before pickup", keys: ["email_notifications", "sms_notifications"] },
  { step: "24-Hour Return Reminder", desc: "Heads-up the day before the vehicle is due back", keys: ["sms_notifications"] },
  { step: "Running Late Workflow", desc: "Automatic overdue escalation, late fees & owner alerts", keys: ["overdue_escalation"] },
  { step: "Extension Offer", desc: "Offers a paid extension instead of a late fee", keys: ["overdue_escalation"] },
  { step: "Vehicle Return", desc: "Return processing, inspection prompts & CRM stage sync", keys: ["ghl_crm_sync"] },
  { step: "Review Request", desc: "Asks happy customers for a review after return", keys: ["review_requests"] },
  { step: "Referral Invitation", desc: "Sends their referral code after the rental — friend gets $15 off, they earn 500 points once the friend completes a paid rental", keys: ["loyalty_referrals"] },
];

const delayLabel = (m) => (m === 0 ? "Instantly" : m < 60 ? `${m} min` : m < 1440 ? `${Math.round(m / 60)} hr` : `${Math.round(m / 1440)} day${m >= 2880 ? "s" : ""}`);

export default function AutopilotView({ token, onNavigate }) {
  const [state, setState] = useState(null);
  const [fu, setFu] = useState(null);
  const [saving, setSaving] = useState(false);
  const H = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/automations`, H).then((r) => setState(r.data)).catch(() => setState({ items: [] }));
    axios.get(`${API}/marketing/followups`, H).then((r) => setFu(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [token]);

  const saveFu = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/marketing/followups`, { enabled: fu.enabled, steps: fu.steps }, H);
      toast.success("Follow-up sequence saved — new leads use it immediately.");
    } catch { toast.error("Could not save sequence."); }
    finally { setSaving(false); }
  };

  const setStep = (i, patch) => setFu({ ...fu, steps: fu.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  if (!state) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading Autopilot…</div>;

  const items = state.items || [];
  const on = (keys) => keys.every((k) => items.find((i) => i.key === k)?.effective !== false);
  const activeCount = JOURNEY.filter((j) => on(j.keys)).length;
  const fuAutomationOn = items.find((i) => i.key === "lead_followups")?.effective !== false;

  return (
    <div data-testid="autopilot-view" className="space-y-6">
      {fu && (
        <div className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-5">
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <Target size={18} className="text-[#F2B705]" />
            <h3 className="font-display font-bold text-white mr-auto">Marketing Autopilot — Lead Follow-Up Sequence</h3>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input data-testid="followups-enabled-toggle" type="checkbox" checked={fu.enabled} onChange={(e) => setFu({ ...fu, enabled: e.target.checked })} className="accent-[#F2B705] w-4 h-4" /> Sequence on
            </label>
          </div>
          <p className="text-white/50 text-sm mb-1">Every open lead with an email automatically gets this sequence until they book or 14 days pass. {fu.sent_total} follow-up{fu.sent_total === 1 ? "" : "s"} sent so far.</p>
          {!fuAutomationOn && <p className="text-red-400 text-xs mb-2">⚠ The "Lead Follow-Up Sequences" master automation is OFF — enable it in Automations for sends to go out.</p>}
          <div className="space-y-3 mt-4">
            {fu.steps.map((s, i) => (
              <div key={s.id} data-testid={`followup-step-${s.id}`} className={`rounded-lg border p-4 ${s.enabled ? "bg-black/30 border-white/10" : "bg-black/20 border-white/5 opacity-60"}`}>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-[#F2B705]/15 text-[#F2B705] text-xs font-bold">{delayLabel(s.delay_min)}</span>
                  <Mail size={13} className="text-white/40" />
                  <input data-testid={`followup-subject-${s.id}`} value={s.subject} onChange={(e) => setStep(i, { subject: e.target.value })}
                    className="flex-1 min-w-[200px] bg-transparent border-b border-white/15 focus:border-[#F2B705] outline-none py-1 text-white text-sm font-semibold" />
                  <label className="flex items-center gap-1.5 text-xs text-white/50 cursor-pointer">
                    <input type="checkbox" checked={s.enabled} onChange={(e) => setStep(i, { enabled: e.target.checked })} className="accent-[#F2B705]" /> on
                  </label>
                </div>
                <textarea data-testid={`followup-body-${s.id}`} rows={2} value={s.body} onChange={(e) => setStep(i, { body: e.target.value })}
                  className="w-full bg-black/40 border border-white/10 focus:border-[#F2B705] rounded-md px-3 py-2 text-white/80 text-xs outline-none resize-y" />
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-white/40 text-[10px] uppercase tracking-wider shrink-0">SMS version</span>
                  <input data-testid={`followup-sms-${s.id}`} value={s.sms || ""} onChange={(e) => setStep(i, { sms: e.target.value })}
                    placeholder="Short text used when the lead only has a phone number (sent via GoHighLevel)"
                    className="flex-1 bg-black/40 border border-white/10 focus:border-[#F2B705] rounded-md px-3 py-1.5 text-white/70 text-xs outline-none" />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button data-testid="followups-save-btn" onClick={saveFu} disabled={saving} className="flex items-center gap-1.5 px-5 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50"><Save size={14} /> {saving ? "Saving…" : "Save Sequence"}</button>
            <span className="text-white/35 text-xs">Placeholders: {"{name} {vehicle} {vehicle_line} {booking_link}"}</span>
          </div>
          {fu.recent_sends?.length > 0 && (
            <div className="mt-5 border-t border-white/10 pt-4">
              <div className="text-white/60 text-xs uppercase tracking-wider mb-2">Recent sends</div>
              <div className="space-y-1.5">
                {fu.recent_sends.slice(0, 8).map((l) => (
                  <div key={l.id} className="flex items-center gap-2 text-xs text-white/50">
                    {l.ok ? <CheckCircle2 size={12} className="text-green-400" /> : <XCircle size={12} className="text-red-400" />}
                    <span className="text-white/75">{l.name}</span> · {l.subject} · <span className="text-white/35">{fmtDateTime(l.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5 flex items-center gap-4">
        <Zap size={22} className="text-[#F2B705]" />
        <div className="mr-auto">
          <h3 className="font-display font-bold text-white">Rental Autopilot</h3>
          <p className="text-white/50 text-sm">The automated customer journey — {activeCount}/{JOURNEY.length} steps running{state.master_paused ? " (MASTER PAUSED)" : ""}.</p>
        </div>
        <button data-testid="autopilot-manage-btn" onClick={() => onNavigate?.("automation")} className="px-4 py-2 rounded-md bg-white/5 border border-white/10 text-white/70 hover:text-white text-sm font-semibold">Manage Toggles</button>
      </div>

      <div className="max-w-2xl mx-auto">
        {JOURNEY.map((j, i) => {
          const active = on(j.keys) && !state.master_paused;
          return (
            <div key={j.step}>
              {i > 0 && <div className="flex justify-center py-0.5"><ArrowDown size={14} className="text-white/20" /></div>}
              <div data-testid={`autopilot-step-${i}`} className={`flex items-center gap-4 rounded-xl border px-5 py-3.5 ${active ? "bg-[#0C0C0C] border-white/10" : "bg-red-950/20 border-red-500/20"}`}>
                {active ? <CheckCircle2 size={18} className="text-green-400 shrink-0" /> : <XCircle size={18} className="text-red-400 shrink-0" />}
                <div>
                  <div className="text-white font-semibold text-sm">{j.step}</div>
                  <div className="text-white/45 text-xs">{j.desc}</div>
                </div>
                <span className={`ml-auto text-[10px] font-bold uppercase tracking-widest ${active ? "text-green-400" : "text-red-400"}`}>{active ? "Auto" : "Off"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
